// Parse a "Resumen de la Incidencia" plain-text template into structured
// fields used by the CreateParticularCaseForm. The template is sectioned with
// emoji headers (👤 / 📱 / ⚠️ AVERÍA / 📍 ORIGEN / 🏁 DESTINO) so we cut the
// raw text into blocks first and then pick individual labelled lines out of
// each block. This avoids ambiguity when "Estado", "Ciudad" or "Dirección"
// appear under both ORIGEN and DESTINO.

const trim = (v) => (typeof v === "string" ? v.trim() : "");

// Strict equality fails when the API returns "MIRANDA" but the agent typed
// "Miranda", or "Vehiculo no enciende" vs "Vehículo no enciende", etc.
// Normalise both sides (lowercase + strip diacritics) and fall back from
// exact → prefix → contains in either direction.
export const normalizeForMatch = (value) =>
  (value || "")
    .toString()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/\s+/g, " ")
    .trim();

export const findOptionLoose = (opts, name) => {
  if (!opts?.length || !name) return null;
  const target = normalizeForMatch(name);
  if (!target) return null;
  const normalized = opts.map((o) => ({ o, n: normalizeForMatch(o?.name) }));
  return (
    normalized.find(({ n }) => n === target)?.o ||
    normalized.find(({ n }) => n.startsWith(target))?.o ||
    normalized.find(({ n }) => target.startsWith(n) && n.length > 2)?.o ||
    normalized.find(({ n }) => n.includes(target))?.o ||
    normalized.find(({ n }) => target.includes(n) && n.length > 2)?.o ||
    null
  );
};

const pickLine = (block, label) => {
  if (!block) return "";
  // Tolerate any non-letter characters (emojis, bullets, spaces, variation
  // selectors) between the line start and the label. We can't use `\s*` here
  // because emojis like 👤 / 📱 / 🆔 are not whitespace, so the agent's
  // template `👤 Cliente: Valeria` would otherwise never match.
  const re = new RegExp(
    `(?:^|\\n)[^A-Za-zÁÉÍÓÚÑáéíóúñ\\n]*${label}\\s*:\\s*([^\\n]+)`,
    "i"
  );
  const match = block.match(re);
  return match ? trim(match[1]) : "";
};

const sliceSection = (text, startMarker, endMarkers = []) => {
  const startIdx = text.search(startMarker);
  if (startIdx === -1) return "";
  const rest = text.slice(startIdx);
  let endIdx = rest.length;
  for (const marker of endMarkers) {
    const idx = rest.slice(1).search(marker);
    if (idx !== -1 && idx + 1 < endIdx) endIdx = idx + 1;
  }
  return rest.slice(0, endIdx);
};

// Split "Valeria Cortez" -> { firstName: "Valeria", lastName: "Cortez" }.
// Multi-word last names ("Maria Jose Perez Lopez") keep the first token as
// firstName and the rest as lastName, which matches what most agents type
// into the existing two-field form.
const splitName = (fullName) => {
  const parts = trim(fullName).split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { firstName: "", lastName: "" };
  if (parts.length === 1) return { firstName: parts[0], lastName: "" };
  return { firstName: parts[0], lastName: parts.slice(1).join(" ") };
};

// Venezuelan phone formats we may receive:
//   "584120278759"        (E.164 without +)
//   "+58 412 0278759"     (E.164 with spaces)
//   "04120278759"         (national)
//   "412-0278759"         (mobile only)
// We normalise to { prefix: "0412", number: "0278759" } because the form's
// `phoneCodes` options are 4-digit strings starting with 0 (0212, 0412, ...).
const splitPhone = (raw) => {
  const digits = trim(raw).replace(/\D/g, "");
  if (!digits) return { prefix: "", number: "" };

  let national = digits;
  if (national.startsWith("58") && national.length >= 11) {
    national = "0" + national.slice(2);
  } else if (!national.startsWith("0") && national.length === 10) {
    national = "0" + national;
  }

  if (national.length < 5) return { prefix: "", number: national };
  return { prefix: national.slice(0, 4), number: national.slice(4) };
};

export const parseIncidenceTemplate = (rawText) => {
  if (!rawText || typeof rawText !== "string") {
    throw new Error("Texto vacío o inválido");
  }

  // Normalise CRLF and collapse non-breaking spaces so the regexes below
  // behave the same regardless of where the text was copied from.
  const text = rawText.replace(/\r\n?/g, "\n").replace(/ /g, " ");

  const AVERIA = /AVER[IÍ]A/i;
  const ORIGEN = /\bORIGEN\b/i;
  const DESTINO = /\bDESTINO\b/i;
  const ENDSECTION = /Hora de solicitud|⏰/i;

  // Templates de afiliados incluyen "RESUMEN DE LA INCIDENCIA"; los de
  // particulares arrancan directamente con el contenido. Si no se encuentra
  // el marcador se usa el inicio del texto como cabecera.
  const headerBlock =
    sliceSection(text, /RESUMEN DE LA INCIDENCIA/i, [
      AVERIA,
      ORIGEN,
      DESTINO,
      ENDSECTION,
    ]) || sliceSection(text, /\S/, [AVERIA, ORIGEN, DESTINO, ENDSECTION]);
  const faultBlock = sliceSection(text, AVERIA, [
    ORIGEN,
    DESTINO,
    ENDSECTION,
  ]);
  const originBlock = sliceSection(text, ORIGEN, [DESTINO, ENDSECTION]);
  const destinyBlock = sliceSection(text, DESTINO, [ENDSECTION]);

  const customerFullName = pickLine(headerBlock, "Cliente");
  const phoneRaw = pickLine(headerBlock, "Teléfono");
  const plate = pickLine(headerBlock, "Placa");

  const { firstName, lastName } = splitName(customerFullName);
  const { prefix: phonePrefix, number: phoneNumber } = splitPhone(phoneRaw);

  const data = {
    customerFullName,
    firstName,
    lastName,
    phonePrefix,
    phoneNumber,
    vehiclePlate: plate.toUpperCase(),
    vehicleBrand: pickLine(headerBlock, "Marca"),
    vehicleModel: pickLine(headerBlock, "Modelo"),
    vehicleYear: pickLine(headerBlock, "Año"),
    vehicleColor: pickLine(headerBlock, "Color"),
    faultType: pickLine(faultBlock, "Tipo"),
    faultDetail: pickLine(faultBlock, "Detalle"),
    originState: pickLine(originBlock, "Estado"),
    originCity: pickLine(originBlock, "Ciudad"),
    originAddress: pickLine(originBlock, "Dirección"),
    originLocationLink: pickLine(originBlock, "Link GPS"),
    destinyState: pickLine(destinyBlock, "Estado"),
    destinyCity: pickLine(destinyBlock, "Ciudad"),
    destinyAddress: pickLine(destinyBlock, "Dirección"),
    destinyLocationLink: pickLine(destinyBlock, "Link GPS"),
  };

  const missing = [];
  if (!data.customerFullName) missing.push("Cliente");
  if (!data.phoneNumber) missing.push("Teléfono");
  if (!data.originAddress) missing.push("Dirección de origen");
  if (missing.length) {
    const err = new Error(
      `Faltan campos críticos en la plantilla: ${missing.join(", ")}`
    );
    err.code = "MISSING_FIELDS";
    err.missing = missing;
    err.partial = data;
    throw err;
  }

  return data;
};
